import { generateClient } from "aws-amplify/api";
import { GraphQLQuery } from '@aws-amplify/api';
import { notifyError, notifySuccess } from "../../../components/ServiceNotifications.tsx";
import { appSyncConfig } from "../config/stripe-api.ts";

const client = generateClient({
    endpoint: appSyncConfig.endpoint,
    region: appSyncConfig.region,
    authMode: 'userPool'
});

export type StripePrice = {
    id: string;
    currency: string;
    product: string;
    unit_amount: number;
    active: boolean;
    nickname?: string;
    billing_scheme?: string;
    recurring?: {
        interval: string;
        interval_count: number;
    };
}

export type CreateStripePrice = {
    createPrice: StripePrice;
}

export type UpdateStripePrice = {
    updatePrice: StripePrice;
}

export type DeleteStripePrice = {
    deletePrice: string;
}

export type GetStripePrice = {
    getPrice: StripePrice;
}

const CREATE_STRIPE_PRICE = `
    mutation createPrice($input: CreatePriceInput!) {
        createPrice(input: $input) {
            id
        }
    }
`;

const UPDATE_STRIPE_PRICE = `
    mutation updatePrice($input: UpdatePriceInput!) {
        updatePrice(input: $input) {
            id
        }
    }
`;

const DELETE_STRIPE_PRICE = `
    mutation deletePrice($input: DeletePriceInput!) {
        deletePrice(input: $input)
    }
`;

const GET_STRIPE_PRICE = `
    query getPrice($id: ID!) {
        getPrice(id: $id) {
            id
        }
    }
`;

export const createStripePrice = async (priceData: {
    currency: string;
    product: string;
    unitAmount: number;
    active?: boolean;
    nickname?: string;
    billingScheme?: string;
    recurring?: {
        interval: string;
        intervalCount?: number;
    };
}) => {
    console.log('Using updated mutation:', CREATE_STRIPE_PRICE);
    try {
        const response = await client.graphql<GraphQLQuery<CreateStripePrice>>({
            query: CREATE_STRIPE_PRICE,
            variables: { 
                input: {
                    currency: priceData.currency,
                    product: priceData.product,
                    unitAmount: priceData.unitAmount,
                    active: priceData.active ?? true,
                    nickname: priceData.nickname,
                    billingScheme: priceData.billingScheme || 'perUnit',
                    recurring: priceData.recurring ? {
                        interval: priceData.recurring.interval,
                        intervalCount: priceData.recurring.intervalCount || 1
                    } : undefined
                }
            },
        });
        notifySuccess('Stripe price created successfully');
        return response.data?.createPrice;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const updateStripePrice = async (id: string, priceData: {
    nickname?: string;
    active?: boolean;
}) => {
    try {
        const response = await client.graphql<GraphQLQuery<UpdateStripePrice>>({
            query: UPDATE_STRIPE_PRICE,
            variables: { input: { id, ...priceData } },
        });
        notifySuccess('Stripe price updated successfully');
        return response.data?.updatePrice;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const deleteStripePrice = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<DeleteStripePrice>>({
            query: DELETE_STRIPE_PRICE,
            variables: { input: { id } },
        });
        notifySuccess('Stripe price deleted successfully');
        return response.data?.deletePrice;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};

export const getStripePrice = async (id: string) => {
    try {
        const response = await client.graphql<GraphQLQuery<GetStripePrice>>({
            query: GET_STRIPE_PRICE,
            variables: { id },
        });
        return response.data?.getPrice;
    } catch (e) {
        notifyError(e);
        throw e;
    }
};